import { createApp, provide, h } from 'vue';
import { createPinia } from 'pinia';
import piniaPluginPersistedstate from 'pinia-plugin-persistedstate';
import App from './App.vue';
import './assets/css/_base.scss';
import { plugin, defaultConfig } from '@formkit/vue';
import { DefaultApolloClient } from '@vue/apollo-composable';
import { useSitecoreGraphQLService } from './foundation/apiSitecoreGraphQLService';
import { createRouter, createWebHistory } from 'vue-router';
import { isProduction } from './foundation/apiConstant';
import { IConfiguration } from './foundation/interfaces/IConfiguration';
import { IDatasource } from './foundation/interfaces/IDatasource';
import { GlobalProperties } from './foundation/enums/GlobalProperties';
import { createVfm } from 'vue-final-modal';
import 'vue-final-modal/style.css';
import animateScrollTo from 'animated-scroll-to';
import { useAnalytics } from './composables/useAnalytics';
import { RoutesEnum } from './foundation/routeMappings';
const { apolloClient } = useSitecoreGraphQLService();
const vfm = createVfm();

export const APP_NAME = 'qmove-app';
const spaOrComponent = App; // development we can still debug/test easily all the components
// Retrieve configuration from Sitecore rendering HTML
const dataProps = document.getElementById(APP_NAME)?.getAttribute('data-properties');
const config: IConfiguration = dataProps != null ? JSON.parse(dataProps).config : null;
const datasource: IDatasource = dataProps != null ? JSON.parse(dataProps).datasource : null;

const app = createApp({
  setup() {
    provide(DefaultApolloClient, apolloClient);
  },
  render: () => h(spaOrComponent),
});
// global analytics to be used by vue components
app.config.globalProperties.$analytics = useAnalytics;
// setup configuration to be accessible everywhere
app.provide(GlobalProperties.ServerConfiguration, config);
//Inject component datasource value (no need to use a store for this)
app.provide(GlobalProperties.Datasource, datasource);

// setup pinia
const pinia = createPinia();
pinia.use(piniaPluginPersistedstate);

// register store
app.use(pinia);

// register formkit
app.use(plugin, defaultConfig);

// register vue-final-modal
app.use(vfm);

// setup router
// default routes
const routes = [
  { path: '/', component: () => import('./components/search/SearchWidget.vue') },
  { path: '/search-results', component: () => import('./views/SearchResults.vue'), name: RoutesEnum.SEARCH_RESULTS },
  { path: '/car-details/:year/:make/:model/:carId', component: () => import('./views/CarDetails.vue') },
  { path: '/car-reviews/:year/:make/:model/:carId', component: () => import('./views/ExpertReviews.vue'), name: RoutesEnum.EXP_REVIEWS },
  { path: '/compare-vehicles', component: () => import('./views/CompareVehicles.vue') },
  { path: '/testing', component: () => import('./views/Testing.vue') },
];

let routerBasePath;
if (isProduction && config?.BasePath) {
  // sitecore to specify the basePath used to initialize the router for routing to work
  // correctly within the component
  let lastIndexOf = null;
  lastIndexOf = config.BasePath.lastIndexOf('/');
  routerBasePath = config.BasePath.substring(0, lastIndexOf); // router base path will be one level up
  routerBasePath = routerBasePath.toLowerCase();
}

const router = createRouter({
  history: createWebHistory(routerBasePath),
  routes,

  scrollBehavior(to) {
    if (to.hash) {
      setTimeout(() => {
        const elementToScrollTo = document.querySelector(to.hash);

        if (elementToScrollTo) {
          animateScrollTo(elementToScrollTo as HTMLElement, {
            minDuration: 300,
            maxDuration: 600,
            verticalOffset: -100,
          });
        }
      }, 500);

      return {
        el: to.hash,
      };
    }
  },
});
app.use(router);

// make sure router is ready otherwise the router won't have the query parameters we need
router.isReady().then(() => {
  app.mount(`#${APP_NAME}`); // mount app on the page
});
